import React, { useState, useEffect } from 'react';
import { BookOpen, Loader2, ArrowLeft, User, Clock, MessageSquare } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { toast } from 'sonner';
import api from '../../api';
import { DueDateBadge } from '../teacher/LessonNotesPage';

export default function ParentLessonNotesPage() {
  const [notes, setNotes]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [loadingNote, setLoadingNote] = useState(false);

  useEffect(() => {
    api.get('/lesson-notes')
      .then(r => setNotes(r.data || []))
      .catch(() => toast.error('Failed to load lesson notes'))
      .finally(() => setLoading(false));
  }, []);

  const openNote = async (noteId) => {
    setLoadingNote(true);
    try {
      const r = await api.get(`/lesson-notes/${noteId}`);
      setSelected(r.data);
    } catch { toast.error('Failed to load note'); }
    finally { setLoadingNote(false); }
  };

  // Group notes by class so parent with multiple children sees them clearly
  const byClass = notes.reduce((acc, note) => {
    const key = note.class_name || 'General';
    if (!acc[key]) acc[key] = [];
    acc[key].push(note);
    return acc;
  }, {});

  return (
    <div className="p-4 lg:p-6 max-w-4xl mx-auto" data-testid="parent-lesson-notes-page">

      {!selected ? (
        <>
          <div className="mb-5">
            <h1 className="text-xl font-bold text-slate-900">Lesson Notes</h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Notes published by your {Object.keys(byClass).length > 1 ? "children's" : "child's"} teacher{Object.keys(byClass).length > 1 ? 's' : ''}
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-blue-600 animate-spin" /></div>
          ) : notes.length === 0 ? (
            <div className="bg-white rounded-2xl border border-slate-200 py-16 text-center">
              <BookOpen className="w-12 h-12 mx-auto mb-3 text-slate-200" />
              <p className="font-semibold text-slate-600">No lesson notes published yet</p>
              <p className="text-sm text-slate-400 mt-1">Notes will appear here once your child's teachers publish them</p>
            </div>
          ) : (
            <div className="space-y-6" data-testid="parent-notes-list">
              {Object.entries(byClass).map(([className, classNotes]) => (
                <div key={className}>
                  {Object.keys(byClass).length > 1 && (
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-3.5 h-3.5 text-blue-600" />
                      </div>
                      <h2 className="text-sm font-bold text-slate-700">{className}</h2>
                      <div className="flex-1 h-px bg-slate-200" />
                      <span className="text-xs text-slate-400">{classNotes.length} note{classNotes.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  <div className="space-y-3">
                    {classNotes.map(note => (
                      <button key={note.id} onClick={() => openNote(note.id)}
                        className="w-full text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-blue-300 hover:shadow-md transition-all group"
                        data-testid={`parent-note-card-${note.id}`}>
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-700 transition-colors">{note.topic}</h3>
                            <p className="text-sm text-slate-500 mt-0.5">{note.subject} · {note.class_name}</p>
                            <div className="flex items-center gap-4 mt-2 flex-wrap">
                              <span className="flex items-center gap-1 text-xs text-slate-400">
                                <User className="w-3 h-3" /> {note.teacher_name}
                              </span>
                              <span className="flex items-center gap-1 text-xs text-slate-400">
                                <Clock className="w-3 h-3" /> {note.created_at?.slice(0, 10)}
                              </span>
                              {note.comment_count > 0 && (
                                <span className="flex items-center gap-1 text-xs text-slate-400">
                                  <MessageSquare className="w-3 h-3" /> {note.comment_count}
                                </span>
                              )}
                              {note.due_date && <DueDateBadge dueDate={note.due_date} />}
                            </div>
                          </div>
                          <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0 group-hover:bg-blue-100 transition-colors">
                            <BookOpen className="w-5 h-5 text-blue-600" />
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      ) : (
        /* ── Note Detail (read-only) ── */
        <div data-testid="parent-note-detail">
          <button onClick={() => setSelected(null)}
            className="flex items-center gap-1.5 text-slate-500 hover:text-slate-700 text-sm mb-5"
            data-testid="back-to-parent-notes">
            <ArrowLeft className="w-4 h-4" /> Back to Notes
          </button>

          {loadingNote ? (
            <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 text-blue-600 animate-spin" /></div>
          ) : (
            <div className="space-y-5">
              {/* Header */}
              <div className="bg-[#2563eb] rounded-2xl p-6 relative overflow-hidden">
                <div className="absolute inset-0 opacity-[0.07]"
                  style={{ backgroundImage: 'radial-gradient(circle,#fff 1px,transparent 1px)', backgroundSize: '20px 20px' }} />
                <div className="relative">
                  <p className="text-blue-200 text-xs font-semibold uppercase tracking-widest mb-1">
                    {selected.subject} · {selected.class_name}
                  </p>
                  <h2 className="text-xl font-bold text-white">{selected.topic}</h2>
                  <p className="text-blue-100 text-sm mt-1">
                    By {selected.teacher_name} · {selected.created_at?.slice(0, 10)}
                  </p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    {selected.timetable_slot?.day && (
                      <span className="inline-flex items-center text-xs font-semibold bg-white/15 text-white border border-white/20 px-2.5 py-0.5 rounded-full">
                        {selected.timetable_slot.day} · {selected.timetable_slot.period}
                      </span>
                    )}
                    {selected.due_date && <DueDateBadge dueDate={selected.due_date} />}
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6">
                <div className="prose prose-sm max-w-none text-slate-700 leading-relaxed">
                  <ReactMarkdown>{selected.content || ''}</ReactMarkdown>
                </div>
              </div>

              {/* Discussion — read-only for parent */}
              {(selected.comments || []).length > 0 && (
                <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6" data-testid="parent-comments-section">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4">
                    Student Discussion ({selected.comments.length})
                  </p>
                  <div className="space-y-4">
                    {selected.comments.filter(c => !c.parent_id).map(comment => {
                      const commentReplies = selected.comments.filter(r => r.parent_id === comment.id);
                      const RoleBadge = ({ role }) => {
                        if (role === 'teacher' || role === 'class_teacher')
                          return <span className="text-[10px] font-bold bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">Teacher</span>;
                        if (role === 'student')
                          return <span className="text-[10px] font-bold bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full">Student</span>;
                        return null;
                      };
                      return (
                        <div key={comment.id}>
                          <div className="flex gap-3">
                            <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <User className="w-4 h-4 text-slate-400" />
                            </div>
                            <div className="flex-1 bg-slate-50 rounded-2xl rounded-tl-none px-4 py-3">
                              <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                <p className="text-sm font-bold text-slate-800">{comment.user_name}</p>
                                <RoleBadge role={comment.user_role} />
                                <p className="text-xs text-slate-400">{comment.created_at?.slice(0, 16).replace('T', ' ')}</p>
                              </div>
                              <p className="text-sm text-slate-700">{comment.content}</p>
                            </div>
                          </div>
                          {commentReplies.map(reply => (
                            <div key={reply.id} className="ml-11 mt-2 flex gap-3">
                              <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                                <User className="w-3.5 h-3.5 text-blue-400" />
                              </div>
                              <div className="flex-1 bg-blue-50 rounded-2xl rounded-tl-none px-4 py-3">
                                <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                                  <p className="text-sm font-bold text-slate-800">{reply.user_name}</p>
                                  <RoleBadge role={reply.user_role} />
                                  <p className="text-xs text-slate-400">{reply.created_at?.slice(0, 16).replace('T', ' ')}</p>
                                </div>
                                <p className="text-sm text-slate-700">{reply.content}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 p-3 bg-slate-50 rounded-xl border border-slate-100 text-center">
                    <p className="text-xs text-slate-400">You are viewing this in read-only mode</p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
